import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ProjectDetails,
  TopViewingReferenceUnit,
  ComparableUnit,
  TowerViewUnit,
  BestByUnitTypeResponse,
  AmenitiesResponse,
} from '../super-picker/services/api';
import HuttonsWhiteLogo from '../img/Huttons_white.png';
import { isUnitStatusNotReleased } from './formatting';
import { ensurePdfUnicodeFont, PDF_UNICODE_FONT } from './pdfUnicodeFont';

// Color palette (matching existing pdfGenerator.ts)
const CORPORATE_COLOR_RGB: [number, number, number] = [26, 35, 126];
const ORANGE_RGB: [number, number, number] = [255, 152, 0];
const DARK_BG_RGB: [number, number, number] = [15, 23, 42];
const FOOTER_BG_RGB: [number, number, number] = [20, 30, 48];
const INFO_BOX_BG_RGB: [number, number, number] = [30, 41, 59];

interface UserInfo {
  Name?: string;
  Mobile?: string;
  Email?: string;
  CEANumber?: string;
  Photo?: string;
}

const loadImageEl = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => reject(new Error('timeout')), 10000);
    img.onload = () => { clearTimeout(timeout); resolve(img); };
    img.onerror = () => { clearTimeout(timeout); reject(new Error('failed')); };
    img.src = url;
  });
};

/** Serializable cell for PDF tower view grid (key = "floor-stack") */
export interface TowerViewGridCell {
  unitNumber: string;
  blockNumber: string | null;
  bedroom: number;
  unitStatus: string | null;
  /** Raw ProjectUnit FloorNumber (e.g. "G", "01") — used for G → "-" labels. */
  floorNumber?: string | null;
  priceNps?: number | null;
  priceDps?: number | null;
  areaSqft?: number | null;
}

export interface ProjectReportData {
  userInfo: UserInfo | null;
  hasPriceMatrixSimulation?: boolean;

  // Project
  projectDetails: ProjectDetails | null;
  projectSectionExpanded: boolean;

  // Reference Unit
  topViewingReferenceUnit: TopViewingReferenceUnit | null;

  // Site Map capture
  siteMapScreenshot: string | null;
  siteMapDimensions: { width: number; height: number } | null;
  sitePlanBedroomFilter: number[];
  sitePlanShowPoints: boolean;

  // Amenities Map capture (shown when project section is expanded)
  amenitiesMapScreenshot: string | null;
  amenitiesMapDimensions: { width: number; height: number } | null;
  amenitiesData: AmenitiesResponse | null;
  amenitiesRadius: number; // in meters, default 1000

  // Tower View: drawn as PDF table (no screenshot) to avoid cutoff
  towerViewGridData: {
    blockNumbers: string[];
    blocks: Record<string, { floors: number[]; stacks: number[]; grid: Record<string, TowerViewGridCell> }>;
  } | null;

  // Loan params for outlay/instalment formula explanation
  loanToValue: number;
  tenureYears: number;
  interestRate: number;

  // Available Units
  availableUnitsVisible: boolean;
  topViewingSortedUnits: Array<TowerViewUnit & { outlay?: number; installment?: number; floor?: number; stack?: number }>;
  /** Executive Condo (Category C1803) — enables NPS/DPS labels in tower/available-units pages. */
  isExecutiveCondo?: boolean;

  // Best PX Bedroom
  bestPxBedroomVisible: boolean;
  bestByUnitTypeData: BestByUnitTypeResponse | null;

  // Comparable / Best-matched Units
  topViewingComparableSectionOpen: boolean;
  topViewingFilteredComparableUnits: ComparableUnit[];

  // Budget Matched Units (Cross Project) (display list = same as UI after NEARBY filter)
  topViewingBudgetSectionOpen: boolean;
  topViewingBudgetRegionTab: string;
  topViewingBudgetSortedUnits: Array<TowerViewUnit & { outlay?: number; installment?: number; floor?: number; stack?: number; distance?: number | null; superScore?: number }>;
  topViewingBudgetRange: { base: number; min: number; max: number; percent: number } | null;
  topViewingBudgetRangeFilters?: Record<string, { min: string; max: string }>;
  topViewingBudgetNearbyRadiusKm?: number;

  // Internal: pre-loaded logo for footer (set by generateProjectReport)
  _logoDataUrl?: string;
  _logoAspect?: number;
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '-';
  return `$${value.toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (value: number | null | undefined, decimals: number = 0): string => {
  if (value == null || isNaN(value)) return '-';
  return value.toLocaleString('en-SG', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const getBedroomLabel = (bedroom: number): string => {
  if (bedroom === -1 || bedroom >= 90) return 'PH';
  return `${bedroom}BR`;
};

const addHeader = (doc: jsPDF, title: string, pageNum: number, totalPages: number, userInfo: UserInfo | null) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  // Orange bar at top
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 8, 'F');
  // Blue header bar
  doc.setFillColor(...CORPORATE_COLOR_RGB);
  doc.rect(0, 8, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.text(title, 10, 18);
  doc.text(`PAGE ${pageNum}/${totalPages}`, pageWidth - 10, 18, { align: 'right' });
  doc.setTextColor(0, 0, 0);
};

const addFooter = (
  doc: jsPDF,
  pageNum: number,
  totalPages: number,
  userInfo: UserInfo | null,
  logoDataUrl?: string,
  logoAspect?: number,
  recordRangeText?: string,
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - 15;
  if (recordRangeText) {
    doc.setTextColor(90, 90, 90);
    doc.setFontSize(7);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    doc.text(recordRangeText, pageWidth / 2, footerY - 3, { align: 'center' });
  }
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont(PDF_UNICODE_FONT, 'normal');
  // Huttons logo
  if (logoDataUrl && logoAspect) {
    const logoH = 7;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', 10, footerY + (15 - logoH) / 2, logoW, logoH);
  } else {
    doc.text('Huttons', 10, footerY + 8);
  }
  doc.text(`${pageNum}`, pageWidth / 2, footerY + 8, { align: 'center' });
  if (userInfo) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    if (userInfo.Name) doc.text(userInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    if (userInfo.CEANumber) doc.text(`CEA: ${userInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
  }
};

const addSimulationWatermark = (doc: jsPDF, text: string = 'GUESS-TIMATED PRICE'): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.saveGraphicsState();
  doc.setGState(new (doc as any).GState({ opacity: 0.12 }));
  doc.setTextColor(70, 70, 70);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.setFontSize(24);
  const xStep = Math.max(70, pageWidth / 3);
  const yStep = Math.max(55, pageHeight / 4);
  for (let y = 30; y <= pageHeight + 30; y += yStep) {
    for (let x = -20; x <= pageWidth + 40; x += xStep) {
      doc.text(text, x, y, {
        align: 'center',
        angle: 330,
      });
    }
  }
  doc.restoreGraphicsState();
};

const loadUserPhoto = async (doc: jsPDF, userInfo: UserInfo, photoX: number, photoY: number, photoRadius: number): Promise<boolean> => {
  if (!userInfo.Photo) return false;
  try {
    const photoPath = userInfo.Photo.startsWith('/') ? userInfo.Photo.substring(1) : userInfo.Photo;
    const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    return await new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), 5000);
      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const size = photoRadius * 2;
            const px = Math.max(256, Math.round(size * 12));
            canvas.width = px;
            canvas.height = px;
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            const r = px / 2;
            const maxInCircle = r * Math.SQRT2;
            const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
            const sw = Math.round(img.width * scale);
            const sh = Math.round(img.height * scale);
            const sx = Math.round((px - sw) / 2);
            const sy = Math.round((px - sh) / 2);
            ctx.save();
            ctx.beginPath();
            ctx.arc(r, r, r, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(img, sx, sy, sw, sh);
            ctx.restore();
            const imgData = canvas.toDataURL('image/png');
            doc.addImage(imgData, 'PNG', photoX - photoRadius, photoY - photoRadius, size, size);
          }
          doc.setDrawColor(...ORANGE_RGB);
          doc.setLineWidth(2);
          doc.circle(photoX, photoY, photoRadius, 'D');
          resolve(true);
        } catch {
          resolve(false);
        }
      };
      img.onerror = () => { clearTimeout(timeout); resolve(false); };
      img.src = photoUrl;
    });
  } catch {
    return false;
  }
};

// ────────────────────────────────────────────────────────────────────
//  COVER PAGE
// ────────────────────────────────────────────────────────────────────
const renderCoverPage = async (doc: jsPDF, data: ProjectReportData) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const { userInfo, projectDetails } = data;

  // Full dark background
  doc.setFillColor(...DARK_BG_RGB);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // Top orange bar
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 8, 'F');

  // Company branding
  let yPos = 25;
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    const logoAspect = logoImg.width / logoImg.height;
    const logoHeight = 10;
    const logoWidth = logoHeight * logoAspect;
    const logoX = (pageWidth - logoWidth) / 2;

    const logoCanvas = document.createElement('canvas');
    logoCanvas.width = logoImg.width;
    logoCanvas.height = logoImg.height;
    const logoCtx = logoCanvas.getContext('2d');
    if (logoCtx) {
      logoCtx.drawImage(logoImg, 0, 0);
      const logoDataUrl = logoCanvas.toDataURL('image/png');
      doc.addImage(logoDataUrl, 'PNG', logoX, yPos - logoHeight + 1, logoWidth, logoHeight);
    } else {
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont(PDF_UNICODE_FONT, 'bold');
      doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
    }
  } catch {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += 8;
  doc.setFontSize(10);
  doc.setFont(PDF_UNICODE_FONT, 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text('SINGAPORE\'S LARGEST PRIVATE REAL ESTATE AGENCY', pageWidth / 2, yPos, { align: 'center' });

  // Orange separator
  yPos += 8;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 1, 'F');

  // Main title
  yPos += 20;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.text('PROJECT OVERVIEW REPORT', pageWidth / 2, yPos, { align: 'center' });

  // Project name
  if (projectDetails?.name) {
    yPos += 15;
    doc.setFontSize(20);
    doc.setTextColor(...ORANGE_RGB);
    doc.text(projectDetails.name, pageWidth / 2, yPos, { align: 'center' });
  }

  // User information block
  yPos += 30;
  const infoBoxWidth = 120;
  const infoBoxHeight = 50;
  const infoBoxX = (pageWidth - infoBoxWidth) / 2;
  const infoBoxY = yPos;

  doc.setFillColor(...INFO_BOX_BG_RGB);
  doc.setDrawColor(...ORANGE_RGB);
  doc.setLineWidth(2);
  doc.roundedRect(infoBoxX, infoBoxY, infoBoxWidth, infoBoxHeight, 3, 3, 'FD');

  if (userInfo) {
    const photoRadius = 18;
    const photoPadding = 10;
    const photoX = infoBoxX + photoPadding + photoRadius;
    const photoY = infoBoxY + infoBoxHeight / 2;

    const photoLoaded = await loadUserPhoto(doc, userInfo, photoX, photoY, photoRadius);
    if (!photoLoaded) {
      doc.setFillColor(...ORANGE_RGB);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(PDF_UNICODE_FONT, 'bold');
      const initials = userInfo.Name ? userInfo.Name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U';
      doc.text(initials, photoX, photoY + 3, { align: 'center' });
    }

    const textStartX = infoBoxX + photoPadding + photoRadius * 2 + 15;
    let detailY = infoBoxY + 12;
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    if (userInfo.Name) { doc.text(userInfo.Name, textStartX, detailY); detailY += 7; }
    doc.setFontSize(9);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    if (userInfo.CEANumber) {
      doc.setTextColor(255, 255, 255);
      const ceaLabel = 'CEA Registration: ';
      doc.text(ceaLabel, textStartX, detailY);
      doc.setTextColor(...ORANGE_RGB);
      doc.setFont(PDF_UNICODE_FONT, 'bold');
      doc.text(userInfo.CEANumber, textStartX + doc.getTextWidth(ceaLabel), detailY);
      doc.setTextColor(255, 255, 255);
      doc.setFont(PDF_UNICODE_FONT, 'normal');
      detailY += 6;
    }
    if (userInfo.Email) { doc.text(userInfo.Email, textStartX, detailY); detailY += 6; }
    if (userInfo.Mobile) { doc.text(userInfo.Mobile, textStartX, detailY); }
  }

  // Generation date
  yPos = infoBoxY + infoBoxHeight + 20;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(PDF_UNICODE_FONT, 'normal');
  const genDate = new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Generated on: ${genDate}`, pageWidth / 2, yPos, { align: 'center' });

  // Separator
  yPos += 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 1, 'F');

  // Disclaimer
  yPos += 12;
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(8);
  doc.setFont(PDF_UNICODE_FONT, 'italic');
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hours = now.getHours();
  const mins = String(now.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  const timestamp = `${dd}/${mm}/${yyyy}, ${hours12}:${mins} ${ampm}`;
  const disclaimer = `Information and materials as of ${timestamp}. Subject to change without prior notice.`;
  const disclaimerLines = doc.splitTextToSize(disclaimer, pageWidth - 40);
  doc.text(disclaimerLines, pageWidth / 2, yPos, { align: 'center' });

  // Cover footer
  const footerY = pageHeight - 15;
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont(PDF_UNICODE_FONT, 'normal');
  // Huttons logo
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    const logoH = 7;
    const logoW = logoH * (logoImg.width / logoImg.height);
    doc.addImage(HuttonsWhiteLogo, 'PNG', 10, footerY + (15 - logoH) / 2, logoW, logoH);
  } catch {
    doc.text('Huttons', 10, footerY + 8);
  }
  doc.text('1', pageWidth / 2, footerY + 8, { align: 'center' });
  if (userInfo) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    if (userInfo.Name) doc.text(userInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    if (userInfo.CEANumber) doc.text(`CEA: ${userInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
  }
};

// ────────────────────────────────────────────────────────────────────
//  PROJECT DETAILS PAGE (Page 2 - always shown)
//  Contains: Basic Project Information + Reference Unit (if any)
// ────────────────────────────────────────────────────────────────────
const renderProjectDetailsPage = (doc: jsPDF, data: ProjectReportData, pageNum: number, totalPages: number) => {
  const pd = data.projectDetails;
  if (!pd) return;
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.addPage();
  addHeader(doc, 'PROJECT INFORMATION', pageNum, totalPages, data.userInfo);
  let yPos = 30;

  // ── Section 1: Basic Project Information ──────────────────────────

  // Project name with accent bar
  doc.setFillColor(...CORPORATE_COLOR_RGB);
  doc.roundedRect(15, yPos - 5, pageWidth - 30, 14, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.text(pd.name || 'N/A', pageWidth / 2, yPos + 3, { align: 'center' });
  yPos += 16;

  // Project info in a clean two-column key-value layout (match on-screen details)
  const detailRows: string[][] = [];
  const addRow = (label: string, value: string | null | undefined) => {
    if (value != null && value !== '') detailRows.push([label, String(value)]);
  };
  addRow('Name', pd.name);
  addRow('Developer', pd.developer);
  addRow('Tenure', pd.tenure);
  addRow('Total Units', pd.totalUnits != null ? String(pd.totalUnits) : null);
  addRow('Completed Date', pd.completedDate ? new Date(pd.completedDate).toLocaleDateString('en-SG', { year: 'numeric', month: '2-digit', day: '2-digit' }) : null);
  addRow('Area', pd.area);
  addRow('District', pd.district);
  addRow('Address', pd.address);

  autoTable(doc, {
    startY: yPos,
    body: detailRows,
    theme: 'plain',
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50, textColor: [100, 100, 100] as [number, number, number] },
      1: { fontStyle: 'bold', textColor: [0, 0, 0] as [number, number, number] },
    },
    styles: { font: PDF_UNICODE_FONT, fontSize: 10, cellPadding: { top: 3, bottom: 3, left: 15, right: 10 } },
    margin: { left: 15, right: 15 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Bedroom stats table (if data available)
  const brKeys: Array<{ key: string; label: string }> = [
    { key: 'br1', label: '1 BR' }, { key: 'br2', label: '2 BR' }, { key: 'br3', label: '3 BR' },
    { key: 'br4', label: '4 BR' }, { key: 'br5', label: '5 BR' }, { key: 'ph', label: 'Penthouse' },
  ];
  const brRows: string[][] = [];
  for (const { key, label } of brKeys) {
    const stats = (pd as any)[key] as { availableUnits: number | null; fromPrice: number | null; fromPsf: number | null } | null | undefined;
    if (stats && stats.availableUnits != null && stats.availableUnits > 0) {
      brRows.push([label, String(stats.availableUnits), formatCurrency(stats.fromPrice), formatCurrency(stats.fromPsf)]);
    }
  }
  if (brRows.length > 0) {
    // Section sub-header with orange accent
    doc.setFillColor(...ORANGE_RGB);
    doc.rect(15, yPos, 3, 8, 'F');
    doc.setFontSize(11);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Bedroom Statistics', 22, yPos + 6);
    yPos += 12;

    autoTable(doc, {
      startY: yPos,
      head: [['Type', 'Available Units', 'From Price', 'From PSF']],
      body: brRows,
      theme: 'striped',
      headStyles: { font: PDF_UNICODE_FONT, fillColor: CORPORATE_COLOR_RGB, textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      styles: { font: PDF_UNICODE_FONT, fontSize: 9, textColor: [0, 0, 0] },
      margin: { left: 15, right: 15 },
    });
    yPos = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Section 2: Reference Unit Information ─────────────────────────

  const ref = data.topViewingReferenceUnit;
  if (ref) {
    // Section sub-header with orange accent
    doc.setFillColor(...ORANGE_RGB);
    doc.rect(15, yPos, 3, 8, 'F');
    doc.setFontSize(11);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Reference Unit', 22, yPos + 6);
    yPos += 14;

    const floor = 'unitNumber' in ref && ref.unitNumber ? ref.unitNumber.match(/#?(\d+)-(\d+)/) : null;

    // Reference unit info in styled card
    autoTable(doc, {
      startY: yPos,
      head: [['Unit No.', 'Block', 'Floor', 'Bedroom', 'Bathroom', 'Area (sqft)', 'Price', 'PSF', 'Status']],
      body: [[
        ref.unitNumber || '-',
        ref.blockNumber || '-',
        floor ? floor[1] : '-',
        String(ref.bedroom || '-'),
        String(ref.bathroom || '-'),
        formatNumber(ref.areaSqft),
        formatCurrency(ref.price),
        formatCurrency(ref.psf),
        ref.unitStatus || '-',
      ]],
      theme: 'grid',
      headStyles: { font: PDF_UNICODE_FONT, fillColor: CORPORATE_COLOR_RGB, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      didParseCell: (cellData: any) => {
        if (cellData.section === 'body' && cellData.row.index === 0) {
          cellData.cell.styles.fillColor = INFO_BOX_BG_RGB;
          cellData.cell.styles.textColor = [255, 255, 255];
          cellData.cell.styles.fontStyle = 'bold';
        }
      },
      styles: { font: PDF_UNICODE_FONT, fontSize: 9, textColor: [0, 0, 0], cellPadding: 3 },
      margin: { left: 15, right: 15 },
    });
  }

  addFooter(doc, pageNum, totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
};

// ────────────────────────────────────────────────────────────────────
//  AMENITIES MAP PAGE — landscape, map screenshot + amenities list
// ────────────────────────────────────────────────────────────────────
const formatDistance = (meters: number): string => {
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)}km away`;
  return `${Math.round(meters)}m away`;
};

const renderAmenitiesMapPage = (doc: jsPDF, data: ProjectReportData, pageNum: number, totalPages: number) => {
  doc.addPage('l'); // landscape for maximum image area
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const radiusLabel = data.amenitiesRadius >= 1000
    ? `${(data.amenitiesRadius / 1000).toFixed(data.amenitiesRadius % 1000 === 0 ? 0 : 1)}km`
    : `${data.amenitiesRadius}m`;
  addHeader(doc, `AMENITIES MAP (${radiusLabel})`, pageNum, totalPages, data.userInfo);
  let yPos = 28;

  // Amenities map screenshot (left side)
  const mapAreaWidth = pageWidth * 0.55;
  const listAreaX = mapAreaWidth + 10;
  const listAreaWidth = pageWidth - listAreaX - 10;

  if (data.amenitiesMapScreenshot) {
    try {
      const naturalWidth = data.amenitiesMapDimensions?.width || 800;
      const naturalHeight = data.amenitiesMapDimensions?.height || 600;
      const aspectRatio = naturalWidth / naturalHeight;

      const usableWidth = mapAreaWidth - 16;
      const maxHeight = pageHeight - yPos - 20;

      let imgWidth = usableWidth;
      let imgHeight = imgWidth / aspectRatio;
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = imgHeight * aspectRatio;
      }

      const imgX = 8;
      doc.addImage(data.amenitiesMapScreenshot, 'JPEG', imgX, yPos, imgWidth, imgHeight);
    } catch (err) {
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(10);
      doc.text('Amenities map capture unavailable', mapAreaWidth / 2, yPos + 20, { align: 'center' });
    }
  }

  // Amenities list (right side)
  if (data.amenitiesData) {
    const amenities = data.amenitiesData.amenities;
    let listY = yPos;

    const renderAmenitySection = (title: string, rawItems: Array<{ name?: string; distance: number; type?: string; line?: string; category?: string }>, color: [number, number, number]) => {
      const items = rawItems.filter(i => i.name && i.name.trim() !== '' && i.name.trim() !== '-');
      if (items.length === 0 || listY > pageHeight - 30) return;

      // Section header
      doc.setFillColor(...color);
      doc.rect(listAreaX, listY, 3, 6, 'F');
      doc.setFontSize(8);
      doc.setFont(PDF_UNICODE_FONT, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`${title} (${items.length})`, listAreaX + 5, listY + 4);
      listY += 8;

      for (const item of items) {
        if (listY > pageHeight - 25) break;
        doc.setFontSize(7);
        doc.setFont(PDF_UNICODE_FONT, 'bold');
        doc.setTextColor(0, 0, 0);
        const name = item.name || 'Unknown';
        const nameLines = doc.splitTextToSize(name, listAreaWidth - 5);
        doc.text(nameLines[0], listAreaX + 5, listY);
        listY += 4;

        doc.setFont(PDF_UNICODE_FONT, 'normal');
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(6);
        const details: string[] = [];
        if (item.type) details.push(item.type);
        if (item.line) details.push(item.line);
        if (item.category) details.push(item.category);
        details.push(formatDistance(item.distance));
        doc.text(details.join(' · '), listAreaX + 5, listY);
        listY += 5;
      }
      listY += 2;
    };

    renderAmenitySection('MRT/LRT', amenities.mrtLrt, [255, 87, 34]);     // orange-red
    renderAmenitySection('Schools', amenities.schools, [76, 175, 80]);     // green
    renderAmenitySection('Preschools', amenities.preschools, [156, 39, 176]); // purple
    renderAmenitySection('Supermarkets', amenities.supermarkets, [255, 152, 0]); // orange
  }

  addFooter(doc, pageNum, totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
};

// ────────────────────────────────────────────────────────────────────
//  SITE MAP PAGE — full-page landscape, white background
// ────────────────────────────────────────────────────────────────────
const renderSiteMapPage = (doc: jsPDF, data: ProjectReportData, pageNum: number, totalPages: number) => {
  doc.addPage('l'); // landscape for maximum image area
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  addHeader(doc, 'SITE MAP', pageNum, totalPages, data.userInfo);
  let yPos = 28;

  // Bedroom filter caption
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.setFont(PDF_UNICODE_FONT, 'italic');
  const filterLabel = data.sitePlanBedroomFilter.length > 0
    ? `Bedroom Filter: ${data.sitePlanBedroomFilter.map(b => getBedroomLabel(b)).join(', ')}`
    : 'All bedrooms shown';
  const pointsLabel = data.sitePlanShowPoints ? '' : ' | Points hidden';
  doc.text(filterLabel + pointsLabel, 10, yPos);
  yPos += 5;

  if (data.siteMapScreenshot) {
    try {
      const naturalWidth = data.siteMapDimensions?.width || 1200;
      const naturalHeight = data.siteMapDimensions?.height || 800;
      const aspectRatio = naturalWidth / naturalHeight;

      const usableWidth = pageWidth - 16;     // 8mm margin each side
      const maxHeight = pageHeight - yPos - 18; // room for footer

      let imgWidth = usableWidth;
      let imgHeight = imgWidth / aspectRatio;
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = imgHeight * aspectRatio;
      }

      const imgX = (pageWidth - imgWidth) / 2;
      doc.addImage(data.siteMapScreenshot, 'JPEG', imgX, yPos, imgWidth, imgHeight);
    } catch (err) {
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(10);
      doc.text('Site map capture unavailable', pageWidth / 2, yPos + 20, { align: 'center' });
    }
  }

  addFooter(doc, pageNum, totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
};

// ────────────────────────────────────────────────────────────────────
//  TOWER VIEW PAGES — draw table in PDF (no screenshot) to avoid cutoff
// ────────────────────────────────────────────────────────────────────
const formatBlockLabel = (block: string): string => {
  if (/^\d+$/.test(block)) return `Blk ${block}`;
  return block;
};

const formatTowerFloorLabel = (floor: number): string => (
  floor < 0 ? `B${Math.abs(floor)}` : String(floor).padStart(2, '0')
);

/** Floor column label: ground ("G") floors export as "-", matching Available Units. */
const resolveTowerFloorLabel = (
  floor: number,
  stacks: number[],
  grid: Record<string, TowerViewGridCell>,
): string => {
  const cells = stacks
    .map((stack) => grid[`${floor}-${stack}`])
    .filter((cell): cell is TowerViewGridCell => !!cell);
  if (
    cells.length > 0
    && cells.every((cell) => String(cell.floorNumber ?? '').trim().toUpperCase() === 'G')
  ) {
    return '-';
  }
  return formatTowerFloorLabel(floor);
};

const renderTowerViewAsTable = (doc: jsPDF, data: ProjectReportData, pageNum: number, totalPages: number): number => {
  const grid = data.towerViewGridData;
  const isEc = data.isExecutiveCondo === true;
  if (!grid || !grid.blockNumbers.length) {
    doc.addPage('l');
    addHeader(doc, 'TOWER VIEW', pageNum, totalPages, data.userInfo);
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(10);
    doc.text('Tower view data unavailable', 148, 110, { align: 'center' });
    addFooter(doc, pageNum, totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
    return 0;
  }

  const filterLabel = data.sitePlanBedroomFilter.length > 0
    ? `Bedroom Filter: ${data.sitePlanBedroomFilter.map(b => getBedroomLabel(b)).join(', ')}`
    : 'All bedrooms shown';

  let pagesAdded = 0;
  const headerSpace = 28;
  const footerSpace = 18;

  for (let b = 0; b < grid.blockNumbers.length; b++) {
    const blockKey = grid.blockNumbers[b];
    const blockData = grid.blocks[blockKey];
    if (!blockData || !blockData.floors.length || !blockData.stacks.length) continue;

    doc.addPage('l');
    const title = `TOWER VIEW - ${formatBlockLabel(blockKey)}`;
    addHeader(doc, title, pageNum + pagesAdded, totalPages, data.userInfo);

    let yPos = headerSpace;
    if (b === 0) {
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont(PDF_UNICODE_FONT, 'italic');
      doc.text(filterLabel, 10, yPos);
      yPos += 6;
    }

    // Build table: rows = floors (desc), first column = floor label, then one column per stack
    const floors = [...blockData.floors].sort((a, b) => b - a);
    const stacks = [...blockData.stacks].sort((a, b) => a - b);
    const colWidths = 18;
    const cellHeight = 10;
    const headerRow = ['Floor', ...stacks.map(s => `Stack ${s}`)];
    const bodyRows: string[][] = floors.map((floor) => {
      const row: string[] = [resolveTowerFloorLabel(floor, stacks, blockData.grid)];
      for (const stack of stacks) {
        const key = `${floor}-${stack}`;
        const cell = blockData.grid[key];
        if (!cell) {
          row.push('-');
          continue;
        }
        const unitLabel = (cell.unitNumber || '').trim().startsWith('#') ? cell.unitNumber : `#${cell.unitNumber}`;
        const isSold = (cell.unitStatus || '').toUpperCase() === 'SOLD';
        const isNotReleased = isUnitStatusNotReleased(cell.unitStatus);
        if (isSold) {
          row.push(`${unitLabel}\n${cell.bedroom}BR\nSOLD`);
        } else if (isNotReleased) {
          row.push(`${unitLabel}\n${cell.bedroom}BR\n-`);
        } else if (isEc) {
          const nps = cell.priceNps != null && cell.priceNps > 0 ? formatCurrency(cell.priceNps) + ' (NPS)' : '-';
          const dps = cell.priceDps != null && cell.priceDps > 0 ? formatCurrency(cell.priceDps) + ' (DPS)' : '-';
          row.push(`${unitLabel}\n${cell.bedroom}BR\n${nps}\n${dps}`);
        } else {
          const price = cell.priceNps != null && cell.priceNps > 0 ? formatCurrency(cell.priceNps) : '-';
          row.push(`${unitLabel}\n${cell.bedroom}BR\n${price}`);
        }
      }
      return row;
    });

    const soldFillColor: [number, number, number] = [239, 154, 154]; // red 200 for sold units (visible)
    const isSoldCell = (rowIndex: number, colIndex: number): boolean => {
      if (colIndex <= 0) return false;
      const floor = floors[rowIndex];
      const stack = stacks[colIndex - 1];
      if (stack == null) return false;
      const key = `${floor}-${stack}`;
      const cell = blockData.grid[key];
      return !!(cell && (cell.unitStatus || '').toUpperCase() === 'SOLD');
    };
    autoTable(doc, {
      startY: yPos,
      head: [headerRow],
      body: bodyRows,
      theme: 'grid',
      headStyles: { font: PDF_UNICODE_FONT, fillColor: CORPORATE_COLOR_RGB, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6 },
      bodyStyles: { fillColor: false },
      columnStyles: Object.fromEntries([
        [0, { cellWidth: 14, fontSize: 6 }],
        ...stacks.map((_, i) => [i + 1, { cellWidth: colWidths, fontSize: 5, minCellHeight: cellHeight }]),
      ]),
      styles: { font: PDF_UNICODE_FONT, fontSize: 5, cellPadding: 1.5 },
      margin: { left: 10, right: 10 },
      didParseCell: (hookData: any) => {
        if (hookData.section === 'body' && hookData.cell?.styles) {
          const rowIndex = hookData.row?.index ?? hookData.row;
          const colIndex = hookData.column?.index ?? hookData.column;
          if (colIndex > 0 && isSoldCell(rowIndex, colIndex)) {
            hookData.cell.styles.fillColor = soldFillColor;
          }
        }
      },
      willDrawCell: (hookData: any) => {
        if (hookData.section === 'body' && hookData.cell && hookData.cursor) {
          const rowIndex = hookData.row?.index ?? hookData.row;
          const colIndex = hookData.column?.index ?? hookData.column;
          if (colIndex <= 0) return;
          if (isSoldCell(rowIndex, colIndex)) {
            const x = hookData.cell.x;
            const y = hookData.cursor.y;
            const width = hookData.cell.width;
            const height = hookData.cell.height;
            if (typeof x === 'number' && typeof y === 'number' && width > 0 && height > 0) {
              doc.setFillColor(...soldFillColor);
              doc.rect(x, y, width, height, 'F');
            }
          }
        }
      },
    });

    if (data.hasPriceMatrixSimulation) {
      addSimulationWatermark(doc);
    }
    addFooter(doc, pageNum + pagesAdded, totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
    pagesAdded++;
  }

  return Math.max(0, pagesAdded - 1); // extra pages beyond first
};

// ────────────────────────────────────────────────────────────────────
//  AVAILABLE UNITS PAGE (no Status column; outlay/instalment formula at top)
// ────────────────────────────────────────────────────────────────────
const AVAILABLE_UNITS_ROWS_PER_PAGE = 30;

const renderAvailableUnitsPages = (doc: jsPDF, data: ProjectReportData, pageNum: number, totalPages: number): number => {
  const ltv = data.loanToValue ?? 75;
  const tenure = data.tenureYears ?? 30;
  const rate = data.interestRate ?? 1.55;
  const monthlyRatePct = (rate / 12).toFixed(4);
  const months = tenure * 12;
  const isEc = data.isExecutiveCondo === true;

  const units = data.topViewingSortedUnits;
  const formatFloorStack = (value: number | null | undefined): string =>
    value != null && value !== 0 ? String(value) : '-';
  const rows = units.map(u => {
    const base = [
      u.unitNumber || '-',
      u.blockNumber || '-',
      formatFloorStack(u.floor),
      formatFloorStack(u.stack),
      String(u.bedroom || '-'),
      String(u.bathroom || '-'),
      formatNumber(u.areaSqft),
      formatCurrency(u.price),
    ];
    if (isEc) {
      base.push(formatCurrency(u.priceDps));
    }
    base.push(
      formatCurrency(u.psf),
      formatCurrency(u.outlay),
      formatCurrency(u.installment),
    );
    return base;
  });

  const head: string[][] = [isEc
    ? ['Unit', 'Block', 'Floor', 'Stack', 'Bed', 'Bath', 'Area', 'Price / NPS', 'DPS', 'PSF', '*Outlay', '*Instalment']
    : ['Unit', 'Block', 'Floor', 'Stack', 'Bed', 'Bath', 'Area', 'Price', 'PSF', '*Outlay', '*Instalment']];
  const moneyColStart = 6;
  const moneyColEnd = isEc ? 11 : 10;
  const chunks: string[][][] = [];
  for (let i = 0; i < rows.length; i += AVAILABLE_UNITS_ROWS_PER_PAGE) {
    chunks.push(rows.slice(i, i + AVAILABLE_UNITS_ROWS_PER_PAGE));
  }

  let pagesAdded = 0;
  const totalUnits = units.length;

  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    if (chunkIndex === 0) {
      doc.addPage();
      addHeader(doc, 'AVAILABLE UNITS', pageNum, totalPages, data.userInfo);
    } else {
      doc.addPage();
      pagesAdded++;
      addHeader(doc, 'AVAILABLE UNITS (CONT.)', pageNum + pagesAdded, totalPages, data.userInfo);
    }

    const startRecord = chunkIndex * AVAILABLE_UNITS_ROWS_PER_PAGE + 1;
    const endRecord = Math.min((chunkIndex + 1) * AVAILABLE_UNITS_ROWS_PER_PAGE, totalUnits);
    const recordRangeText = `${startRecord}–${endRecord} of ${totalUnits}`;

    autoTable(doc, {
      startY: 28,
      head,
      body: chunks[chunkIndex],
      theme: 'striped',
      headStyles: { font: PDF_UNICODE_FONT, fillColor: CORPORATE_COLOR_RGB, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      columnStyles: Object.fromEntries(
        Array.from({ length: moneyColEnd - moneyColStart + 1 }, (_, i) => [
          moneyColStart + i,
          { halign: 'right' as const },
        ]),
      ),
      didParseCell: (hookData: any) => {
        if (hookData.section === 'head') {
          const col = hookData.column?.index ?? hookData.column;
          if (typeof col === 'number' && col >= moneyColStart && col <= moneyColEnd && hookData.cell?.styles) {
            hookData.cell.styles.halign = 'right';
          }
        }
      },
      styles: { font: PDF_UNICODE_FONT, fontSize: 7, textColor: [0, 0, 0], cellPadding: 2 },
      margin: { left: 10, right: 10, bottom: 26 },
      didDrawPage: () => {
        if (data.hasPriceMatrixSimulation) {
          addSimulationWatermark(doc);
        }
        addFooter(
          doc,
          pageNum + chunkIndex,
          totalPages,
          data.userInfo,
          data._logoDataUrl,
          data._logoAspect,
          recordRangeText,
        );
      },
    });
  }

  // Caption and formula notes after the table
  let noteY = (doc as any).lastAutoTable.finalY + 5;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.text(`Total: ${units.length} units`, 10, noteY);
  noteY += 10;

  doc.setFont(PDF_UNICODE_FONT, 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7);
  doc.text('*Outlay = Down payment (25% of selling price) + Buyer\'s Stamp Duty (BSD).', 10, noteY, { maxWidth: 190 });
  noteY += 6;
  doc.text('*Instalment formula', 10, noteY, { maxWidth: 190 });
  noteY += 5;
  doc.text('= PMT(loan amount, monthly rate, tenure)', 10, noteY, { maxWidth: 190 });
  noteY += 5;
  doc.text(`Loan = Price × ${ltv}% (LTV)`, 10, noteY, { maxWidth: 190 });
  noteY += 5;
  doc.text(`Rate = ${rate}% / 12 = ${monthlyRatePct}%`, 10, noteY, { maxWidth: 190 });
  noteY += 5;
  doc.text(`Tenure = ${tenure} years (${months} months)`, 10, noteY, { maxWidth: 190 });

  return pagesAdded;
};

// ────────────────────────────────────────────────────────────────────
//  BEST PX BEDROOM TYPE PAGE
// ────────────────────────────────────────────────────────────────────
const renderBestPxBedroomPages = (doc: jsPDF, data: ProjectReportData, pageNum: number, totalPages: number): number => {
  if (!data.bestByUnitTypeData?.unitTypes?.length) return 0;

  const showPsf = data.bestByUnitTypeData?.showPsfInTowerView !== false;

  doc.addPage();
  addHeader(doc, 'BEST PRICE BY BEDROOM TYPE', pageNum, totalPages, data.userInfo);
  let yPos = 32;
  let pagesAdded = 0;
  const pageHeight = doc.internal.pageSize.getHeight();

  for (const unitType of data.bestByUnitTypeData.unitTypes) {
    if (!unitType.units?.length) continue;

    // Check if we need a new page
    if (yPos > pageHeight - 60) {
      addFooter(doc, pageNum + pagesAdded, totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
      doc.addPage();
      pagesAdded++;
      addHeader(doc, 'BEST PRICE BY BEDROOM TYPE (CONT.)', pageNum + pagesAdded, totalPages, data.userInfo);
      yPos = 32;
    }

    // Section header
    doc.setFontSize(10);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(`${unitType.unitType}${unitType.description ? ` - ${unitType.description}` : ''}`, 15, yPos);
    yPos += 5;
    const rows = unitType.units.map((u) => {
      const base = [
        u.unitNumber || '-',
        u.blockNumber || '-',
        u.type || '-',
        formatNumber(u.totalAreaSqft),
        formatCurrency(u.sellingPrice),
      ];
      return showPsf ? [...base, formatCurrency(u.psf)] : base;
    });

    autoTable(doc, {
      startY: yPos,
      head: [showPsf
        ? ['Unit', 'Block', 'Type', 'Area (sqft)', 'Price', 'PSF']
        : ['Unit', 'Block', 'Type', 'Area (sqft)', 'Price']],
      body: rows,
      theme: 'striped',
      headStyles: { font: PDF_UNICODE_FONT, fillColor: CORPORATE_COLOR_RGB, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      columnStyles: showPsf
        ? { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } }
        : { 3: { halign: 'right' }, 4: { halign: 'right' } },
      styles: { font: PDF_UNICODE_FONT, fontSize: 8, textColor: [0, 0, 0] },
      margin: { left: 15, right: 15 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;
  }

  addFooter(doc, pageNum + pagesAdded, totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
  return pagesAdded;
};

// ────────────────────────────────────────────────────────────────────
//  BEST-MATCHED UNITS PAGE
// ────────────────────────────────────────────────────────────────────
const renderComparableUnitsPages = (doc: jsPDF, data: ProjectReportData, pageNum: number, totalPages: number): number => {
  doc.addPage();

  const ref = data.topViewingReferenceUnit;
  const bedroomLabel = ref ? getBedroomLabel(ref.bedroom) : '';
  const sectionTitle = `BEST-MATCHED ${bedroomLabel} UNITS`;
  addHeader(doc, sectionTitle, pageNum, totalPages, data.userInfo);

  const units = data.topViewingFilteredComparableUnits;
  const rows = units.map(u => [
    u.projectName || '-',
    u.unitNumber || '-',
    u.blockNumber || '-',
    String(u.bedroom || '-'),
    String(u.bathroom || '-'),
    formatNumber(u.areaSqft),
    formatCurrency(u.price),
    formatCurrency(u.psf),
    formatCurrency(u.outlay),
    formatCurrency(u.installment),
  ]);

  let pagesAdded = 0;
  autoTable(doc, {
    startY: 32,
    head: [['Project', 'Unit', 'Block', 'Bed', 'Bath', 'Area', 'Price', 'PSF', 'Outlay', 'Instalment']],
    body: rows,
    theme: 'striped',
    headStyles: { font: PDF_UNICODE_FONT, fillColor: CORPORATE_COLOR_RGB, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    columnStyles: {
      0: { cellWidth: 35 },
      5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
      8: { halign: 'right' }, 9: { halign: 'right' },
    },
    styles: { font: PDF_UNICODE_FONT, fontSize: 7, textColor: [0, 0, 0], cellPadding: 2 },
    margin: { left: 10, right: 10 },
    didDrawPage: (hookData: any) => {
      if (hookData.pageNumber > 1) {
        pagesAdded++;
        addHeader(doc, `${sectionTitle} (CONT.)`, pageNum + pagesAdded, totalPages, data.userInfo);
      }
      addFooter(doc, pageNum + (hookData.pageNumber - 1), totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
    },
  });

  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.text(`Total: ${units.length} comparable units`, 10, (doc as any).lastAutoTable.finalY + 5);

  return pagesAdded;
};

// ────────────────────────────────────────────────────────────────────
//  BUDGET MATCHED UNITS PAGE
// ────────────────────────────────────────────────────────────────────
const renderBudgetUnitsPages = (doc: jsPDF, data: ProjectReportData, pageNum: number, totalPages: number): number => {
  doc.addPage();
  addHeader(doc, 'BUDGET MATCHED UNITS (CROSS PROJECT)', pageNum, totalPages, data.userInfo);
  let yPos = 28;

  // Filters applied (same as UI)
  const rangeFilters = data.topViewingBudgetRangeFilters ?? {};
  const filterParts: string[] = [];
  filterParts.push(`Region: ${data.topViewingBudgetRegionTab}`);
  if (data.topViewingBudgetRange) {
    filterParts.push(`Budget: ${formatCurrency(data.topViewingBudgetRange.min)} - ${formatCurrency(data.topViewingBudgetRange.max)}`);
  }
  if (data.topViewingBudgetRegionTab === 'NEARBY' && data.topViewingBudgetNearbyRadiusKm != null) {
    filterParts.push(`Nearby radius: ${Number(data.topViewingBudgetNearbyRadiusKm).toFixed(1)} km`);
  }
  const rangeLabels: Record<string, string> = {
    floor: 'Floor',
    stack: 'Stack',
    bedroom: 'Bedroom',
    bathroom: 'Bath',
    areaSqft: 'Area (sqft)',
    price: 'Price',
    psf: 'PSF',
    outlay: 'Outlay',
    installment: 'Instalment',
    distance: 'Distance (km)',
    walkingTimeToMrt: 'Walking time to MRT (min)',
  };
  Object.entries(rangeFilters).forEach(([key, { min, max }]) => {
    const label = rangeLabels[key] ?? key;
    if (min.trim() !== '' || max.trim() !== '') {
      if (min.trim() !== '' && max.trim() !== '') {
        filterParts.push(`${label}: ${min} - ${max}`);
      } else if (min.trim() !== '') {
        filterParts.push(`${label}: >= ${min}`);
      } else {
        filterParts.push(`${label}: <= ${max}`);
      }
    }
  });

  doc.setFontSize(8);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Filters applied:', 10, yPos);
  yPos += 5;
  doc.setFont(PDF_UNICODE_FONT, 'normal');
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(7);
  doc.text(filterParts.join('  |  '), 10, yPos, { maxWidth: 190 });
  yPos += 8;

  const units = data.topViewingBudgetSortedUnits;
  const rows = units.map(u => [
    u.projectName || '-',
    u.unitNumber || '-',
    u.blockNumber || '-',
    String(u.bedroom || '-'),
    String(u.bathroom || '-'),
    formatNumber(u.areaSqft),
    formatCurrency(u.price),
    formatCurrency(u.psf),
    formatCurrency(u.outlay),
    formatCurrency(u.installment),
    u.distance != null ? `${formatNumber(u.distance, 1)} km` : '-',
  ]);

  let pagesAdded = 0;
  autoTable(doc, {
    startY: yPos,
    head: [['Project', 'Unit', 'Block', 'Bed', 'Bath', 'Area', 'Price', 'PSF', 'Outlay', 'Instalment', 'Distance']],
    body: rows,
    theme: 'striped',
    headStyles: { font: PDF_UNICODE_FONT, fillColor: CORPORATE_COLOR_RGB, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    columnStyles: {
      0: { cellWidth: 30 },
      5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
      8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right' },
    },
    styles: { font: PDF_UNICODE_FONT, fontSize: 7, textColor: [0, 0, 0], cellPadding: 2 },
    margin: { left: 10, right: 10 },
    didDrawPage: (hookData: any) => {
      if (hookData.pageNumber > 1) {
        pagesAdded++;
        addHeader(doc, 'BUDGET MATCHED UNITS (CROSS PROJECT) (CONT.)', pageNum + pagesAdded, totalPages, data.userInfo);
      }
      addFooter(doc, pageNum + (hookData.pageNumber - 1), totalPages, data.userInfo, data._logoDataUrl, data._logoAspect);
    },
  });

  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.text(`Total: ${units.length} budget-matched units`, 10, (doc as any).lastAutoTable.finalY + 5);

  return pagesAdded;
};

// ────────────────────────────────────────────────────────────────────
//  MAIN ENTRY POINT
// ────────────────────────────────────────────────────────────────────
export const generateProjectReport = async (data: ProjectReportData): Promise<void> => {
  try {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    await ensurePdfUnicodeFont(doc);

    // Pre-load Huttons logo for footer
    try {
      const logoImg = await loadImageEl(HuttonsWhiteLogo);
      data._logoDataUrl = HuttonsWhiteLogo;
      data._logoAspect = logoImg.width / logoImg.height;
    } catch { /* fallback to text */ }

    // Calculate total pages (estimate; multi-page tables may add more)
    let totalPages = 1; // cover page
    if (data.projectDetails) totalPages++; // page 2: basic project info + reference unit always shown
    if (data.projectSectionExpanded && (data.amenitiesMapScreenshot || data.amenitiesData)) totalPages++; // amenities map
    if (data.siteMapScreenshot) totalPages++;
    const towerViewBlockCount = data.towerViewGridData?.blockNumbers?.length ?? 0;
    if (towerViewBlockCount > 0) totalPages += towerViewBlockCount;
    if (data.availableUnitsVisible && data.topViewingSortedUnits.length > 0) totalPages++;
    if (data.bestPxBedroomVisible && data.bestByUnitTypeData?.unitTypes?.length) totalPages++;
    if (data.topViewingComparableSectionOpen && data.topViewingFilteredComparableUnits.length > 0) totalPages++;
    if (data.topViewingBudgetSectionOpen && data.topViewingBudgetSortedUnits.length > 0) totalPages++;

    // Estimate extra pages for large tables (rough: 30 rows per page)
    const estimateExtraPages = (count: number) => Math.max(0, Math.ceil(count / 30) - 1);
    if (data.availableUnitsVisible) totalPages += estimateExtraPages(data.topViewingSortedUnits.length);
    if (data.topViewingComparableSectionOpen) totalPages += estimateExtraPages(data.topViewingFilteredComparableUnits.length);
    if (data.topViewingBudgetSectionOpen) totalPages += estimateExtraPages(data.topViewingBudgetSortedUnits.length);

    let currentPage = 1;

    // Page 1: Cover
    await renderCoverPage(doc, data);
    currentPage++;

    // Page 2: Basic project info + reference unit (always shown when project is loaded)
    if (data.projectDetails) {
      renderProjectDetailsPage(doc, data, currentPage, totalPages);
      currentPage++;
    }

    // Amenities Map (conditional — shown when project section is expanded)
    if (data.projectSectionExpanded && (data.amenitiesMapScreenshot || data.amenitiesData)) {
      renderAmenitiesMapPage(doc, data, currentPage, totalPages);
      currentPage++;
    }

    // Site Map (conditional)
    if (data.siteMapScreenshot) {
      renderSiteMapPage(doc, data, currentPage, totalPages);
      currentPage++;
    }

    // Page 4: Tower View — drawn as PDF table (one page per block)
    if (data.towerViewGridData && data.towerViewGridData.blockNumbers.length > 0) {
      renderTowerViewAsTable(doc, data, currentPage, totalPages);
      currentPage += towerViewBlockCount;
    }

    // Page 5: Available Units OR Best PX Bedroom
    if (data.availableUnitsVisible && data.topViewingSortedUnits.length > 0) {
      const extra = renderAvailableUnitsPages(doc, data, currentPage, totalPages);
      currentPage += 1 + extra;
    } else if (data.bestPxBedroomVisible && data.bestByUnitTypeData?.unitTypes?.length) {
      const extra = renderBestPxBedroomPages(doc, data, currentPage, totalPages);
      currentPage += 1 + extra;
    }

    // Page 6: Best-Matched / Comparable Units (conditional)
    if (data.topViewingComparableSectionOpen && data.topViewingFilteredComparableUnits.length > 0) {
      const extra = renderComparableUnitsPages(doc, data, currentPage, totalPages);
      currentPage += 1 + extra;
    }

    // Page 7: Budget Matched Units (Cross Project) (conditional)
    if (data.topViewingBudgetSectionOpen && data.topViewingBudgetSortedUnits.length > 0) {
      const extra = renderBudgetUnitsPages(doc, data, currentPage, totalPages);
      currentPage += 1 + extra;
    }

    // Save PDF
    const projectName = (data.projectDetails?.name || 'Project').replace(/\s+/g, '_');
    const fileName = `Project_Report_${projectName}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Project Report PDF Generation Error:', error);
    throw error;
  }
};
